import { useCallback, useEffect, useMemo, useState } from "react";

import {
  useGetCommsTextTemplatesCountQuery,
  useLazyGetCommsTextTemplatesQuery
} from "services/suno-comms/suno-comms-api";
import { TemplateSourceType } from "services/suno-comms/types/text-templates.type";
import {
  useGetTextTemplatesCountQuery,
  useLazyGetTextTemplatesQuery
} from "services/text-templates";
import {
  TextTemplate,
  TextTemplateFilters
} from "services/types/text-templates.type";

import { commsTemplateToTextTemplate } from "./text-templates-v2.utils";

// The backend caps every list request at 300 (CustomLimitOffsetPagination.
// max_limit). Browse (no search) loads a full 300-row page so practices with
// <=300 templates see their whole category tree on open; "Show more" pages the
// rest. Search uses a smaller page since matches are narrow and render as a flat
// list (ENG-1426).
export const TEMPLATE_PICKER_BROWSE_PAGE_SIZE = 300;
export const TEMPLATE_PICKER_SEARCH_PAGE_SIZE = 25;

const PROJECTION = "{id,name,type,category}";

type UseTemplatePickerArgs = {
  templateTypes?: number[];
  search: string;
  /** Which backend to read templates from. Omitted ⇒ "default" (main backend). */
  templateSource?: TemplateSourceType;
  /**
   * When false, the hook stays inert (no queries, empty list). Used by the
   * merged Leads picker to keep the Comms instance dormant on non-Leads
   * surfaces while still calling the hook unconditionally (rules of hooks).
   */
  enabled?: boolean;
};

export type UseTemplatePickerResult = {
  templates: TextTemplate[];
  totalCount?: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
};

/**
 * Fetches templates for the note/report picker with server-side search,
 * type filtering, and offset pagination. Pages accumulate into a single list
 * so the category tree grows as the user clicks "Show more"; changing the
 * search term or type filter resets back to the first page.
 */
export const useTemplatePicker = ({
  templateTypes,
  search,
  templateSource = "default",
  enabled = true
}: UseTemplatePickerArgs): UseTemplatePickerResult => {
  const isLeads = templateSource === "leads";

  // Memoize on a primitive key so an unstable `templateTypes` array identity
  // from the parent doesn't retrigger the fetch effect on every render.
  const typeKey = templateTypes?.length ? templateTypes.join(",") : "";
  const filters = useMemo<Pick<TextTemplateFilters, "type" | "search">>(
    () => ({
      type: typeKey ? typeKey.split(",").map(Number) : undefined,
      search: search || undefined
    }),
    [typeKey, search]
  );

  // Only the active source's queries fire — the other is skipped — so we never
  // hit the wrong backend or double-count.
  const { data: defaultCount } = useGetTextTemplatesCountQuery(filters, {
    skip: !enabled || isLeads
  });
  const { data: commsCount } = useGetCommsTextTemplatesCountQuery(filters, {
    skip: !enabled || !isLeads
  });
  const totalCount = isLeads ? commsCount : defaultCount;

  const [triggerDefaultTemplates] = useLazyGetTextTemplatesQuery();
  const [triggerCommsTemplates] = useLazyGetCommsTextTemplatesQuery();
  const [templates, setTemplates] = useState<TextTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchPage = useCallback(
    (offset: number): Promise<TextTemplate[]> => {
      const limit = filters.search
        ? TEMPLATE_PICKER_SEARCH_PAGE_SIZE
        : TEMPLATE_PICKER_BROWSE_PAGE_SIZE;
      if (isLeads) {
        // Suno Comms ignores RestQL projection and returns full rows, so we omit
        // `query` and normalize the UUID-id payload to the TextTemplate shape.
        return triggerCommsTemplates({ ...filters, limit, offset })
          .unwrap()
          .then((rows) => rows.map(commsTemplateToTextTemplate))
          .catch(() => [] as TextTemplate[]);
      }
      return triggerDefaultTemplates({
        ...filters,
        query: PROJECTION,
        limit,
        offset
      })
        .unwrap()
        .catch(() => [] as TextTemplate[]);
    },
    [isLeads, triggerDefaultTemplates, triggerCommsTemplates, filters]
  );

  // Reset to the first page whenever the search term or type filter changes.
  useEffect(() => {
    if (!enabled) {
      setTemplates([]);
      setIsLoading(false);
      return;
    }
    let active = true;
    setIsLoading(true);
    fetchPage(0).then((page) => {
      if (active) {
        setTemplates(page);
        setIsLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [fetchPage, enabled]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    const page = await fetchPage(templates.length);
    setTemplates((prev) => [...prev, ...page]);
    setIsLoadingMore(false);
  }, [fetchPage, templates.length, isLoadingMore]);

  const hasMore =
    !isLoading && totalCount !== undefined && templates.length < totalCount;

  return {
    templates,
    totalCount,
    hasMore,
    isLoading,
    isLoadingMore,
    loadMore
  };
};
